import isEqual from 'lodash/isEqual';
import isPlainObject from 'lodash/isPlainObject';

/**
 * Builds a JSON-merge-patch body from a before/after pair, for endpoints that
 * treat an absent key as "keep the stored value".
 *
 * - unchanged key -> omitted
 * - changed key -> included with the new value
 * - key that no longer has a value -> included as `null` (clear)
 *
 * `undefined` and `null` are treated as the same "no value" on both sides, so a
 * field the form never populated does not get cleared on the server.
 *
 * Keys listed in `nestedMergeKeys` are diffed one level deeper instead of being
 * replaced wholesale, matching backends that merge those objects key by key
 * (e.g. `customFields`). A nested object that is entirely unchanged is omitted.
 */
export const buildMergePatch = <T extends Record<string, any>>(
  original: Partial<T>,
  updated: Partial<T>,
  nestedMergeKeys: (keyof T & string)[] = [],
): Partial<T> => {
  const patch: Record<string, unknown> = {};
  const keys = new Set([...Object.keys(original), ...Object.keys(updated)]);

  for (const key of keys) {
    const before = original[key] ?? null;
    const after = updated[key] ?? null;

    if (isEqual(before, after)) {
      continue;
    }

    if (
      nestedMergeKeys.includes(key) &&
      isPlainObject(before) &&
      isPlainObject(after)
    ) {
      const nested = buildMergePatch(
        before as Record<string, unknown>,
        after as Record<string, unknown>,
      );
      if (Object.keys(nested).length > 0) {
        patch[key] = nested;
      }
      continue;
    }

    patch[key] = after;
  }

  return patch as Partial<T>;
};
